import { RouteObject } from 'react-router-dom';

import Dashboard from 'business/dashboard/pages/dashboard-page';
import {
  DataAnalysisGraphPage,
  dataAnalysisGraphPageLoader,
} from 'business/data-analysis/pages/graph';
import ConnectedPage from 'business/layout/connectedPage';
import ReportAggregatedConnectedPage from 'business/layout/report-aggregated-connected-page';
import ReportConnectedPage from 'business/layout/report-connected-page';
import RingComplianceConnectedPage from 'business/layout/ring-compliance-connected-page';
import MultiModuleRoute from 'business/multi-module-route';
import ExcavationBuildTimePage from 'business/production-and-performance/pages/excavation-build-time';
import PlanningPage from 'business/production-and-performance/pages/planning-page';
import RingPerDayPage from 'business/production-and-performance/pages/ring-per-day';
import RingTablePage from 'business/production-and-performance/pages/ring-table';
import ProtectedRouteWithPermissions from 'business/protected-route-with-permissions';
import {
  AnonymousRoute,
  ProtectedRoute,
} from 'business/protectedRouteContainer';
import { BoardPreviewPage } from 'business/quality-control/pages/board-preview';
import { DefectReportHistoryPage } from 'business/quality-control/pages/defect-report-history';
import { RecordSegmentDefectPage } from 'business/quality-control/pages/record-segment-defect';
import { RingComplianceReportPage } from 'business/quality-control/pages/ring-compliance-report';
import { RingComplianceReportDetailPage } from 'business/quality-control/pages/ring-compliance-report-detail';
import { SegmentHistoryPage } from 'business/quality-control/pages/segment-history';
import { SegmentHistoryScanPage } from 'business/quality-control/pages/segment-history/mobile/pages/segment-scan-page';
import { SegmentImportPage } from 'business/quality-control/pages/segment-import';
import { SegmentImportWarningsPage } from 'business/quality-control/pages/segment-import/pages/warnings';
import { IndicatorAnalyze } from 'business/shift-report/indicators/pages/indicator-analyze';
import ReportAggregatedPage from 'business/shift-report/report/pages/ReportAggregated';
import ReportCreatePage from 'business/shift-report/report/pages/ReportCreate';
import ReportEditPage from 'business/shift-report/report/pages/ReportEdit';
import ReportEditEndPage from 'business/shift-report/report/pages/ReportEditEnd';
import ReportEditFinishingPage from 'business/shift-report/report/pages/ReportEditFinishing';
import ReportExportAggregatedPage from 'business/shift-report/report/pages/ReportExportAggregated';
import ReportExportBatchPage from 'business/shift-report/report/pages/ReportExportBatch';
import ReportListPage from 'business/shift-report/report/pages/ReportList';
import ReportListInProgressPage from 'business/shift-report/report/pages/ReportListInProgress';
import ReportListToValidatePage from 'business/shift-report/report/pages/ReportListToValidate';
import ReportListValidatedPage from 'business/shift-report/report/pages/ReportListValidated';
import ReportViewPage from 'business/shift-report/report/pages/ReportView';
import ActivityAnalyze from 'business/shift-report/report-analyze/pages/activity-analyze';
import ProductionAnalyze from 'business/shift-report/report-analyze/pages/production-analyze';
import TaskExportPage from 'business/shift-report/task/pages/TaskExport';
import SteeringTablePage from 'business/steering/pages/table-page';
import { AnalyzeShiftReportDashboardPage } from 'business/user/pages/Dashboard/analyze';
import { VisualizeShiftReportDashboardPage } from 'business/user/pages/Dashboard/visualize';
import LandingPage from 'business/user/pages/landing';
import LoginProviderCallbackScreen from 'business/user/pages/loginProviderCallback';
import NoConstructionSite from 'business/user/pages/noConstructionSite';
import ShiftReportHome from 'business/user/pages/shiftReportHome';
import UnauthorizedPage from 'business/user/pages/unauthorized';
import { ROLES } from 'business/user/services/config';
import Routes from 'config/routes';
import { Module_Enum } from 'generated/graphql';
import ReportingNoMatch from 'technical/router/switch/reporting-no-match';

import RedirectionHome from './redirection-home';

export const commonRoutesProps: RouteObject[] = [
  {
    element: <AnonymousRoute />,
    children: [
      {
        path: Routes.App,
        element: <LandingPage />,
      },
      {
        path: Routes.LoginCallback,
        element: <LoginProviderCallbackScreen />,
      },
    ],
  },

  {
    path: '*',
    element: <ReportingNoMatch />,
  },
];

export const errorRoutes: RouteObject[] = [
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: Routes.NO_CONSTRUCTION_SITE,
        element: <NoConstructionSite />,
      },
      {
        path: Routes.UNAUTHORIZED,
        element: <UnauthorizedPage />,
      },
    ],
  },

  {
    path: Routes.NOT_FOUND,
    element: <ReportingNoMatch />,
  },
];

export const noConstructionSite = [
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: Routes.Home,
        element: <NoConstructionSite />,
      },
    ],
  },
];

export const homeAndDashboardRoutes: RouteObject[] = [
  {
    path: Routes.Home,
    element: <RedirectionHome />,
  },
  {
    element: <ConnectedPage module={Module_Enum.ShiftReport} />,
    children: [
      {
        path: Routes.ReportUserHome, // create report for shift user, switch between create or list
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[ROLES.USER]}
          >
            <ShiftReportHome />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.ReportDashboardVisualize,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <VisualizeShiftReportDashboardPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.ReportDashboardAnalyze,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <AnalyzeShiftReportDashboardPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
  {
    element: <ConnectedPage module={Module_Enum.Dashboard} />,
    children: [
      {
        path: Routes.Dashboard,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.Dashboard}
            allowedRoles={[ROLES.CONSTRUCTION_SITE_READER]}
          >
            <MultiModuleRoute>
              <Dashboard />
            </MultiModuleRoute>
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
];

const dataAnalysisRoutes: RouteObject[] = [
  {
    element: <ConnectedPage module={Module_Enum.DataAnalysis} />,
    children: [
      {
        path: Routes.DataAnalysisGraph,
        loader: dataAnalysisGraphPageLoader,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.DataAnalysis}
            allowedRoles={[ROLES.CONSTRUCTION_SITE_READER]}
          >
            <DataAnalysisGraphPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
];

const prodAndPerfRoutes: RouteObject[] = [
  {
    element: <ConnectedPage module={Module_Enum.ProdPerf} />,
    children: [
      {
        path: Routes.ProdPerfRingList,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ProdPerf}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.CONSTRUCTION_SITE_MANAGER,
            ]}
          >
            <RingTablePage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.ProdPerfRingPerDay,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ProdPerf}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.CONSTRUCTION_SITE_MANAGER,
            ]}
          >
            <RingPerDayPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.ProdPerfExcavationBuildTime,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ProdPerf}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.CONSTRUCTION_SITE_MANAGER,
            ]}
          >
            <ExcavationBuildTimePage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.ProdPerfPlanning,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ProdPerf}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.CONSTRUCTION_SITE_MANAGER,
            ]}
          >
            <PlanningPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
];

const shiftReportRoutes: RouteObject[] = [
  {
    element: <ConnectedPage module={Module_Enum.ShiftReport} />,
    children: [
      // ReportCreate
      {
        path: Routes.ReportCreate,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[ROLES.CONSTRUCTION_SITE_MANAGER]}
          >
            <ReportCreatePage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ReportEditFinishing
      {
        path: Routes.ReportEditFinishing,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[ROLES.USER, ROLES.CONSTRUCTION_SITE_MANAGER]}
          >
            <ReportEditFinishingPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ReportEditEnd
      {
        path: Routes.ReportEditEnd,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[ROLES.USER, ROLES.CONSTRUCTION_SITE_MANAGER]}
          >
            <ReportEditEndPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
  {
    element: <ReportConnectedPage />,
    children: [
      // ReportView
      {
        path: Routes.ReportView,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.USER,
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <ReportViewPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ReportEdit
      {
        path: Routes.ReportEdit,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              // Available in read for full reader
              // Inside components permission check prevent reader to edit
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.USER,
            ]}
          >
            <ReportEditPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
];

const shiftReportListRoutes: RouteObject[] = [
  {
    element: <ConnectedPage module={Module_Enum.ShiftReport} />,
    children: [
      // ReportList
      {
        path: Routes.ReportList,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.USER,
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
            ]}
          >
            <ReportListPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ReportListInProgress
      {
        path: Routes.ReportListInProgress,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
            ]}
          >
            <ReportListInProgressPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ReportListToValidate
      {
        path: Routes.ReportListToValidate,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
            ]}
          >
            <ReportListToValidatePage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ReportListValidated
      {
        path: Routes.ReportListValidated,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <ReportListValidatedPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
];

const shiftReportAnalysisRoutes: RouteObject[] = [
  {
    element: <ConnectedPage module={Module_Enum.ShiftReport} />,
    children: [
      // ReportExportAggregated
      {
        path: Routes.ReportExportAggregated,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <ReportExportAggregatedPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ReportExportBatch
      {
        path: Routes.ReportExportBatch,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <ReportExportBatchPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // TaskExport
      {
        path: Routes.TaskExport,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <TaskExportPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ActivityAnalyze
      {
        path: Routes.ActivityAnalyze,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
            ]}
          >
            <ActivityAnalyze />
          </ProtectedRouteWithPermissions>
        ),
      },
      // ProductionAnalyze
      {
        path: Routes.ProductionAnalyze,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
            ]}
          >
            <ProductionAnalyze />
          </ProtectedRouteWithPermissions>
        ),
      },
      // Indicators
      {
        path: Routes.Indicators,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <IndicatorAnalyze />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
  {
    element: <ReportAggregatedConnectedPage />,
    children: [
      // ReportAggregated
      {
        path: Routes.ReportAggregated,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.ShiftReport}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_FULL_READER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <ReportAggregatedPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
];

const steeringRoutes: RouteObject[] = [
  {
    element: <ConnectedPage module={Module_Enum.ProdPerf} />,
    children: [
      {
        path: Routes.SteeringTable,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.Steering}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.CONSTRUCTION_SITE_MANAGER,
            ]}
          >
            <SteeringTablePage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
];

const qualityControlRoutes: RouteObject[] = [
  {
    element: <ConnectedPage module={Module_Enum.QualityControl} />,
    children: [
      {
        path: Routes.QualitySegmentHistory,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityScan}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <SegmentHistoryPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.QualitySegmentHistoryScan,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityScan}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <SegmentHistoryScanPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.QualitySegmentImport,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityScan}
            allowedRoles={[ROLES.CONSTRUCTION_SITE_MANAGER]}
          >
            <SegmentImportPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.QualitySegmentImportWarnings,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityScan}
            allowedRoles={[ROLES.CONSTRUCTION_SITE_MANAGER]}
          >
            <SegmentImportWarningsPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.QualityRecordSegmentDefect,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityControl}
            allowedRoles={[ROLES.CONSTRUCTION_SITE_MANAGER]}
          >
            <RecordSegmentDefectPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.QualityDefectReportHistory,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityControl}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_READER,
            ]}
          >
            <DefectReportHistoryPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.QualityRingComplianceReport,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityControl}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.USER,
            ]}
          >
            <RingComplianceReportPage />
          </ProtectedRouteWithPermissions>
        ),
      },
      {
        path: Routes.QualityBoardPreview,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityControl}
            allowedRoles={[ROLES.DATA_MANAGER]}
          >
            <BoardPreviewPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
  {
    element: <RingComplianceConnectedPage />,
    children: [
      {
        path: Routes.QualityRingComplianceReportDetail,
        element: (
          <ProtectedRouteWithPermissions
            allowedModule={Module_Enum.QualityControl}
            allowedRoles={[
              ROLES.CONSTRUCTION_SITE_MANAGER,
              ROLES.CONSTRUCTION_SITE_READER,
              ROLES.USER,
            ]}
          >
            <RingComplianceReportDetailPage />
          </ProtectedRouteWithPermissions>
        ),
      },
    ],
  },
];

export const allModulesRoutes = [
  ...shiftReportRoutes,
  ...shiftReportListRoutes,
  ...shiftReportAnalysisRoutes,
  ...prodAndPerfRoutes,
  ...dataAnalysisRoutes,
  ...steeringRoutes,
  ...qualityControlRoutes,
];
